/**
 * BP-RC1 Phase 1 — domainRouteConfig schema, normalization, and validation.
 * @see doc/architecture/BP-RC1-ROUTE-CONFIG-CONTRACT.md §2
 */

import { omitUndefined } from '@shared/utils/omitUndefined';
import type { ExperienceAreaId } from '@shared/frontend-experience-architecture-and-navigation-canon';

/** Must match `FIELDS_AND_LAYOUT_SEGMENT` in domainRouteConfig.ts */
const FIELDS_AND_LAYOUT_SEGMENT = 'fields-and-layout' as const;

export type PageType =
  | 'overview'
  | 'collection'
  | 'record'
  | 'builder'
  | 'queue'
  | 'config'
  | 'detail';

export type HiddenRouteReason =
  | 'detail-sub-route'
  | 'modal-only'
  | 'redirect'
  | 'deprecated-legacy'
  | 'internal';

/** Ant Design icon export name — resolved to ReactNode at navigation generation time. */
export type IconKey = string;

export type PermissionKey = string;

export interface BreadcrumbSegment {
  label: string;
  href?: string;
}

export interface DomainRouteConfigEntry {
  path: string;
  businessDomain: string;
  /** Legacy label — prefer `label` when both exist. */
  title: string;

  /** BP-RC1 Phase 1 — optional until entry migration (Stream P1-B+). */
  id?: string;
  pageType?: PageType;
  label?: string;
  icon?: IconKey;
  menuVisible?: boolean;
  hiddenRouteReason?: HiddenRouteReason;
  permissions?: PermissionKey[];
  surfaceId?: string;
  /** Detail route workbench surface — defaults to `surfaceId` when omitted. */
  recordSurfaceId?: string;
  nonWorkbenchReason?: string;
  breadcrumb?: BreadcrumbSegment[];
  subRoutes?: DomainSubRouteConfig[];

  emptyMessage?: string;
  group?: 'event_definition' | 'event_execution';
  legacy?: boolean;
  removeAfter?: string;
  sunsetNotice?: string;
  detailRouteParam?: string;
  fieldsAndLayout?: boolean;
  /** Sidebar — admin-only menu leaf (system footer). */
  adminOnly?: boolean;
  /** Route mounted from router/index.tsx — config is menu SSOT only. */
  indexMounted?: boolean;
  /** Resolver guard wrapper (system plane). */
  routeGuard?: 'system-admin' | 'system-audit';
  /**
   * Primary/advanced navigation area (frozen canon —
   * `@shared/frontend-experience-architecture-and-navigation-canon`).
   * Declarative annotation only (N1 minimum areaId field): the executable
   * source of truth for which sidebar area a route's menu leaf renders under
   * remains `purposeCenteredNavigation.ts`'s explicit builder wiring. This
   * field documents that mapping on the route entry itself and is not yet
   * consumed to derive nav — populate it incrementally without introducing a
   * second, drift-prone truth for area membership.
   */
  areaId?: ExperienceAreaId;
}

/** @deprecated Use DomainRouteConfigEntry — kept for backward-compatible imports. */
export type DomainSubRoute = DomainRouteConfigEntry;

export interface DomainSubRouteConfig extends Omit<DomainRouteConfigEntry, 'subRoutes'> {
  parentId: string;
}

export type DomainRouteConfigMap = Record<string, readonly DomainRouteConfigEntry[]>;

export interface RouteDerivationContext {
  domainTop: string;
  /** Relative route path segment (e.g. `report`, `report/:id`). */
  routePath: string;
  isDashboard?: boolean;
  isDetail?: boolean;
  isFieldsAndLayout?: boolean;
  isRedirect?: boolean;
}

export interface NormalizedDomainRouteEntry {
  id: string;
  path: string;
  absolutePath: string;
  domainTop: string;
  businessDomain: string;
  pageType: PageType;
  label: string;
  icon: IconKey | null;
  menuVisible: boolean;
  hiddenRouteReason?: HiddenRouteReason;
  permissions: PermissionKey[];
  surfaceId?: string;
  nonWorkbenchReason?: string;
  breadcrumb?: BreadcrumbSegment[];
  detailRouteParam: string;
  fieldsAndLayout: boolean;
  legacy?: boolean;
  adminOnly?: boolean;
  indexMounted?: boolean;
  routeGuard?: 'system-admin' | 'system-audit';
  areaId?: ExperienceAreaId;
}

const PAGE_TYPE_VALUES: ReadonlySet<string> = new Set([
  'overview',
  'collection',
  'record',
  'builder',
  'queue',
  'config',
  'detail',
]);

export function isPageType(value: string): value is PageType {
  return PAGE_TYPE_VALUES.has(value);
}

export function resolveRouteLabel(entry: DomainRouteConfigEntry): string {
  return entry.label ?? entry.title;
}

export function resolveRouteId(
  domainTop: string,
  entry: DomainRouteConfigEntry,
  suffix = ''
): string {
  const base = entry.id ?? `${domainTop}:${entry.path}`;
  return suffix ? `${base}:${suffix}` : base;
}

export function resolveRoutePermissions(entry: DomainRouteConfigEntry): PermissionKey[] {
  if (entry.permissions?.length) {
    return [...entry.permissions];
  }
  return [`${entry.businessDomain}:read`];
}

export function inferPageType(
  entry: DomainRouteConfigEntry,
  context: RouteDerivationContext
): PageType {
  if (context.isRedirect) {
    return 'overview';
  }
  if (context.isDashboard) {
    return 'overview';
  }
  if (context.isFieldsAndLayout) {
    return 'config';
  }
  if (context.isDetail) {
    return 'record';
  }
  if (entry.pageType) {
    return entry.pageType;
  }
  return 'collection';
}

export function resolveMenuVisible(entry: DomainRouteConfigEntry): boolean {
  if (entry.menuVisible !== undefined) {
    return entry.menuVisible;
  }
  if (entry.legacy) {
    return false;
  }
  return true;
}

export function resolveHiddenRouteReason(
  entry: DomainRouteConfigEntry,
  context: RouteDerivationContext,
  menuVisible: boolean
): HiddenRouteReason | undefined {
  if (entry.hiddenRouteReason) {
    return entry.hiddenRouteReason;
  }
  if (menuVisible) {
    return undefined;
  }
  if (entry.legacy) {
    return 'deprecated-legacy';
  }
  if (context.isDetail) {
    return 'detail-sub-route';
  }
  if (context.isFieldsAndLayout) {
    return 'internal';
  }
  if (context.isRedirect) {
    return 'redirect';
  }
  return 'internal';
}

export function resolveNonWorkbenchReason(entry: DomainRouteConfigEntry): string | undefined {
  if (entry.surfaceId) {
    return undefined;
  }
  if (entry.nonWorkbenchReason) {
    return entry.nonWorkbenchReason;
  }
  return 'legacy-page';
}

export function buildAbsolutePath(domainTop: string, routePath: string): string {
  const normalizedRoutePath = routePath.replace(/^\/+/, '');
  if (!normalizedRoutePath) {
    return `/${domainTop}`;
  }
  return `/${domainTop}/${normalizedRoutePath}`.replace(/\/+/g, '/');
}

export function isDomainRouteEntryMigrated(entry: DomainRouteConfigEntry): boolean {
  return (
    !!entry.pageType &&
    !!entry.permissions?.length &&
    (!!entry.surfaceId || !!entry.nonWorkbenchReason) &&
    (entry.menuVisible !== false || !!entry.hiddenRouteReason)
  );
}

export function normalizeDomainRouteEntry(
  domainTop: string,
  entry: DomainRouteConfigEntry,
  context: RouteDerivationContext
): NormalizedDomainRouteEntry {
  const pageType = inferPageType(entry, context);
  const menuVisible =
    context.isDetail || context.isFieldsAndLayout ? false : resolveMenuVisible(entry);
  const hiddenRouteReason = menuVisible
    ? undefined
    : resolveHiddenRouteReason(entry, context, menuVisible);
  const absolutePath = buildAbsolutePath(domainTop, context.routePath);

  return omitUndefined({
    id: resolveRouteId(
      domainTop,
      entry,
      context.isDetail ? 'detail' : context.isFieldsAndLayout ? 'config' : ''
    ),
    path: context.routePath,
    absolutePath,
    domainTop,
    businessDomain: entry.businessDomain,
    pageType,
    label: resolveRouteLabel(entry),
    icon: entry.icon ?? null,
    menuVisible,
    hiddenRouteReason,
    permissions: resolveRoutePermissions(entry),
    surfaceId: entry.surfaceId,
    nonWorkbenchReason: resolveNonWorkbenchReason(entry),
    breadcrumb: entry.breadcrumb,
    detailRouteParam: entry.detailRouteParam ?? 'id',
    fieldsAndLayout: entry.fieldsAndLayout !== false,
    ...(entry.legacy ? { legacy: true } : {}),
    ...(entry.adminOnly ? { adminOnly: true } : {}),
    ...(entry.indexMounted ? { indexMounted: true } : {}),
    ...(entry.routeGuard ? { routeGuard: entry.routeGuard } : {}),
    ...(entry.areaId ? { areaId: entry.areaId } : {}),
  });
}

export function validateNormalizedRouteEntry(entry: NormalizedDomainRouteEntry): string[] {
  const errors: string[] = [];

  if (!isPageType(entry.pageType)) {
    errors.push(`route ${entry.id}: invalid pageType '${entry.pageType}'`);
  }
  if (!entry.permissions.length) {
    errors.push(`route ${entry.id}: permissions must not be empty`);
  }
  if (!entry.menuVisible && !entry.hiddenRouteReason) {
    errors.push(`route ${entry.id}: menuVisible=false requires hiddenRouteReason`);
  }
  if (!entry.surfaceId && !entry.nonWorkbenchReason) {
    errors.push(`route ${entry.id}: requires surfaceId or nonWorkbenchReason`);
  }

  return errors;
}

function isOverviewRouteEntry(entry: DomainRouteConfigEntry): boolean {
  return entry.path === 'dashboard' || entry.pageType === 'overview';
}

function hasParameterizedRoutePath(routePath: string): boolean {
  return routePath.includes(':');
}

export interface ExpandDomainRouteEntriesOptions {
  includeDashboard?: boolean;
  includeDetailRoutes?: boolean;
  includeFieldsAndLayout?: boolean;
  /** Domain-specific overview route (e.g. Knowledge dashboard). */
  overviewRoute?: DomainRouteConfigEntry;
  /** Emit `/domainTop` index redirect entry for parity gates. */
  includeIndexRedirect?: boolean;
}

export function expandDomainRouteEntries(
  domainTop: string,
  routes: readonly DomainRouteConfigEntry[],
  options?: ExpandDomainRouteEntriesOptions
): NormalizedDomainRouteEntry[] {
  const includeDashboard = options?.includeDashboard ?? true;
  const includeDetailRoutes = options?.includeDetailRoutes ?? true;
  const includeFieldsAndLayout = options?.includeFieldsAndLayout ?? true;
  const expanded: NormalizedDomainRouteEntry[] = [];

  if (options?.includeIndexRedirect) {
    const anchor = routes[0];
    expanded.push(
      normalizeDomainRouteEntry(
        domainTop,
        {
          path: '',
          businessDomain: anchor?.businessDomain ?? domainTop,
          title: domainTop,
          pageType: 'overview',
          menuVisible: false,
          hiddenRouteReason: 'redirect',
          permissions: anchor ? resolveRoutePermissions(anchor) : [`${domainTop}:read`],
          nonWorkbenchReason: 'domain-index-redirect',
        },
        {
          domainTop,
          routePath: '',
          isRedirect: true,
        }
      )
    );
  }

  if (includeDashboard) {
    const dashboardFromRoutes = routes.find((route) => route.path === 'dashboard');
    const firstCollectionRoute = routes.find((route) => !isOverviewRouteEntry(route));
    const overview =
      options?.overviewRoute ??
      dashboardFromRoutes ??
      ({
        path: 'dashboard',
        businessDomain: firstCollectionRoute?.businessDomain ?? domainTop,
        title: '대시보드',
        pageType: 'overview',
        menuVisible: true,
        permissions: [`${firstCollectionRoute?.businessDomain ?? domainTop}:read`],
        nonWorkbenchReason: 'domain-dashboard',
        fieldsAndLayout: false,
      } satisfies DomainRouteConfigEntry);

    expanded.push(
      normalizeDomainRouteEntry(domainTop, overview, {
        domainTop,
        routePath: overview.path,
        isDashboard: true,
      })
    );
  }

  for (const route of routes) {
    if (route.path === 'dashboard' && includeDashboard) {
      continue;
    }

    expanded.push(
      normalizeDomainRouteEntry(domainTop, route, {
        domainTop,
        routePath: route.path,
      })
    );

    if (
      includeDetailRoutes &&
      !isOverviewRouteEntry(route) &&
      route.pageType !== 'builder' &&
      !hasParameterizedRoutePath(route.path)
    ) {
      const detailParam = route.detailRouteParam ?? 'id';
      const detailSurfaceId = route.recordSurfaceId ?? route.surfaceId;
      expanded.push(
        normalizeDomainRouteEntry(
          domainTop,
          {
            ...route,
            pageType: 'record',
            menuVisible: false,
            hiddenRouteReason: 'detail-sub-route',
            ...(detailSurfaceId !== undefined ? { surfaceId: detailSurfaceId } : {}),
          },
          {
            domainTop,
            routePath: `${route.path}/:${detailParam}`,
            isDetail: true,
          }
        )
      );
    }

    if (
      includeFieldsAndLayout &&
      route.fieldsAndLayout !== false &&
      !isOverviewRouteEntry(route) &&
      !hasParameterizedRoutePath(route.path)
    ) {
      const { surfaceId: _surfaceId, ...routeWithoutSurface } = route;
      expanded.push(
        normalizeDomainRouteEntry(
          domainTop,
          {
            ...routeWithoutSurface,
            pageType: 'config',
            menuVisible: false,
            hiddenRouteReason: 'internal',
            nonWorkbenchReason: 'standalone-builder',
            label: `${resolveRouteLabel(route)} · Fields & Layout`,
          },
          {
            domainTop,
            routePath: `${route.path}/${FIELDS_AND_LAYOUT_SEGMENT}`,
            isFieldsAndLayout: true,
          }
        )
      );
    }
  }

  return expanded;
}
